import { orderSourceLabel } from '@/utils/orderSource';
/**
 * printRunManifest / printDriverManifest — printable delivery manifests.
 *
 * printRunManifest:    loaded records belonging to one run.
 * printDriverManifest: loaded records matching a driver's name, each run
 *                      rendered as its own block (same layout as a single-run
 *                      manifest) stacked sequentially.
 *
 * Layout: portrait letter, two-line rows. Column 1 fixed-width signature
 * thumbnail box that visually spans both lines on the right.
 *
 * Header: shop logo + name, title, driver, date.
 * Body:   per run — summary pills, one entry per delivered/on-route/undelivered
 *         stop (table-style, sorted by delivery time), then a "Failed
 *         Deliveries" detail section with reason + notes + time.
 */
import { toast } from 'sonner';
import { API } from '@/config/constants';
import { escapeHtml as esc } from '@/utils/htmlEscape';

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch { return ''; }
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
};

const fmtGeneratedAt = () => {
  const d = new Date();
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

const orderNumberShort = (o) => {
  const full = o.internal_order_number || o.order_number || '';
  // Strip the YYYYMMDD- prefix on internal numbers; show just the suffix.
  const m = String(full).match(/-(\d+)$/);
  return m ? m[1] : full;
};

const FAILURE_REASON_LABELS = {
  nobody_home: 'Nobody home',
  wrong_address: 'Wrong address',
  other: 'Other',
};

// Delivery timestamp with a field-name fallback chain that handles legacy POD
// records stored under `confirmed_at` before the rename to `time_delivered` /
// `time_confirmed`.
const deliveryTimeOf = (o) => {
  const dc = o.delivery_confirmation || {};
  return dc.time_delivered || dc.time_confirmed || dc.confirmed_at || '';
};

const renderRow = (o, signatureUrls) => {
  const status = o.status;
  const delivered = status === 'delivered';
  const dc = o.delivery_confirmation || {};
  const time = delivered ? fmtTime(deliveryTimeOf(o)) : (status === 'on_route' ? 'En route' : 'Pending');
  const orderNo = esc(orderNumberShort(o));
  const source = esc(orderSourceLabel(o, '—'));
  const recipient = esc(o.recipient_name || '');
  const street = esc([o.recipient_street_address, o.recipient_street_address_2].filter(Boolean).join(' '));
  const cityStateZip = esc([o.recipient_city, o.recipient_state, o.recipient_zip_code].filter(Boolean).join(', '));
  const address = [street, cityStateZip].filter(Boolean).join(', ');
  const phone = esc(o.recipient_phone || '');
  // "Signed by" — the name of the person who actually signed/received.
  // Optionally annotate with relationship (e.g., "Sarah (wife)").
  const recvName = (dc.received_by || '').trim();
  const recvRel = (dc.received_by_relationship || '').trim();
  const signedBy = recvName
    ? esc(recvRel ? `${recvName} (${recvRel})` : recvName)
    : '';
  const notes = esc(dc.notes || '');

  // Find a signature media file. The caller pre-resolves storage paths into
  // data URLs (since the popup window can't authenticate to /delivery-media).
  let sigSrc = '';
  const sigFile = (dc.media_files || []).find(m => m.file_type === 'signature');
  if (sigFile?.storage_path && signatureUrls[sigFile.storage_path]) {
    sigSrc = signatureUrls[sigFile.storage_path];
  }

  return `
<tr class="row">
  <td class="ord">${orderNo}<div class="src">${source}</div></td>
  <td class="time">${esc(time)}</td>
  <td class="recip"><div class="name">${recipient}</div><div class="addr">${esc(address)}</div></td>
  <td class="meta">${signedBy ? `<div class="signedby">${signedBy}</div>` : ''}<div class="phone">${phone || '<span class="muted">—</span>'}</div>${notes ? `<div class="notes">${notes}</div>` : ''}</td>
  <td class="sig">${sigSrc ? `<img src="${esc(sigSrc)}" alt="Signature" />` : `<span class="muted">${sigFile ? 'Signature unavailable' : 'No signature'}</span>`}</td>
</tr>`;
};

const renderFailedSection = (failedOrders) => failedOrders.length === 0 ? '' : `
<section class="failures">
  <h2>Failed Deliveries (${failedOrders.length})</h2>
  ${failedOrders.map(o => {
    const fail = o.delivery_failure || {};
    const orderNo = esc(orderNumberShort(o));
    const recipient = esc(o.recipient_name || '');
    const street = esc([o.recipient_street_address, o.recipient_street_address_2].filter(Boolean).join(' '));
    const cityStateZip = esc([o.recipient_city, o.recipient_state, o.recipient_zip_code].filter(Boolean).join(', '));
    const address = [street, cityStateZip].filter(Boolean).join(', ');
    const reason = esc(FAILURE_REASON_LABELS[fail.reason] || fail.reason || 'No reason recorded');
    const details = esc(fail.details || '');
    const time = fmtTime(fail.failed_at);
    return `
    <div class="failure">
      <div class="failure-head">
        <strong>#${orderNo} · ${recipient}</strong>
        <span class="failure-time">${esc(time) || '—'}</span>
      </div>
      <div class="failure-addr">${esc(address)}</div>
      <div class="failure-reason"><span class="failure-label">Reason:</span> ${reason}</div>
      ${details ? `<div class="failure-notes"><span class="failure-label">Notes:</span> ${details}</div>` : ''}
    </div>`;
  }).join('')}
</section>`;

/**
 * Render one manifest block: summary pills + a delivery table (sorted by
 * delivery time ascending, undelivered to the bottom) + a failed-deliveries
 * section. Used once for a run manifest, once per run for a driver manifest.
 */
const renderManifestBody = (orders, signatureUrls) => {
  // Bucket: failed go to the bottom section; everything else goes in the table.
  const tableOrders = [];
  const failedOrders = [];
  for (const o of orders) {
    if (o.status === 'failed') failedOrders.push(o);
    else tableOrders.push(o);
  }

  tableOrders.sort((a, b) => {
    const ta = deliveryTimeOf(a);
    const tb = deliveryTimeOf(b);
    if (ta && tb) return ta.localeCompare(tb);
    if (ta) return -1;
    if (tb) return 1;
    return 0;
  });

  const deliveredCount = orders.filter(o => o.status === 'delivered').length;
  const failedCount = failedOrders.length;
  const totalStops = orders.length;

  return `
<div class="summary">
  <span class="pill"><strong>${totalStops}</strong> stops</span>
  <span class="pill delivered"><strong>${deliveredCount}</strong> delivered</span>
  ${failedCount > 0 ? `<span class="pill failed"><strong>${failedCount}</strong> failed</span>` : ''}
</div>

<table>
  <thead>
    <tr>
      <th>Order #</th>
      <th>Time</th>
      <th>Recipient · Address</th>
      <th>Signed by · Phone · Notes</th>
      <th class="sig">Signature</th>
    </tr>
  </thead>
  <tbody>
    ${tableOrders.length === 0
      ? '<tr><td colspan="5" style="text-align:center; padding:20px; color:#888;">No deliveries to display</td></tr>'
      : tableOrders.map(o => renderRow(o, signatureUrls)).join('')}
  </tbody>
</table>

${renderFailedSection(failedOrders)}`;
};

// Wrap a rendered inner body in the full print document (shop header + footer +
// owned automatic-print schedule) and publish it in a popup.
export const scheduleManifestPrint = (popup, isCurrent = () => true, onSettled = () => {}) => {
  let cancelled = false;
  let queued = false;
  let printTimer;
  let fallbackTimer;
  const images = Array.from(popup.document.querySelectorAll('img'));
  const listeners = [];
  const cancelAutoPrint = () => {
    if (cancelled) return;
    cancelled = true;
    clearTimeout(printTimer);
    clearTimeout(fallbackTimer);
    listeners.forEach(([image, done]) => {
      image.removeEventListener('load', done);
      image.removeEventListener('error', done);
    });
    onSettled();
  };
  const go = () => {
    if (cancelled || popup.closed || !isCurrent()) { cancelAutoPrint(); return; }
    cancelAutoPrint();
    try { popup.focus(); popup.print(); } catch { /* Preview remains usable. */ }
  };
  const queue = () => {
    if (queued || cancelled) return;
    queued = true;
    clearTimeout(fallbackTimer);
    printTimer = setTimeout(go, images.length ? 150 : 200);
  };
  let pending = images.length;
  if (!pending) queue();
  else {
    fallbackTimer = setTimeout(go, 3000);
    images.forEach((image) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        pending -= 1;
        if (!pending) queue();
      };
      listeners.push([image, done]);
      image.addEventListener('load', done);
      image.addEventListener('error', done);
      if (image.complete) done();
    });
  }
  return { cancelAutoPrint };
};

const renderSnapshotScope = (scope) => scope ? `
<section class="snapshot-scope">
  <strong>Loaded-record snapshot</strong> · ${esc(scope.includedCount)} included orders
  <div>${esc(scope.selection)}. Uses loaded records, not necessarily every visible screen filter.</div>
  <div>Name search: ${esc(scope.nameSearch || 'None')} · Address search: ${esc(scope.addressSearch || 'None')}</div>
  <div>Screen source filter: ${esc(scope.sourceFilter || 'all')} (not applied to this print selection).</div>
  ${scope.limitReached ? '<div><strong>The 200-order load limit was reached; additional matches may exist.</strong></div>' : ''}
  ${scope.missingRunOrders ? `<div><strong>${esc(scope.missingRunOrders)} selected-run orders are absent from the loaded records.</strong></div>` : ''}
  <div>This snapshot does not certify a complete run or day.</div>
</section>` : '';

const openManifestDocument = ({ docTitle, metaTitle, driverName, date, shop, innerHtml, popup, isCurrent = () => true, scope, onAutoPrintSettled }) => {
  const shopName = esc((shop && shop.name) || 'Las Vegas Bouquet');
  const shopAddress = esc((shop && shop.address) || '');
  const logoSrc = shop && shop.logo_id
    ? `${API}/public/shop-logo?v=${encodeURIComponent(shop.logo_id)}`
    : null;

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(docTitle)}</title>
<style>
  @page { size: letter portrait; margin: 0.4in; }
  html, body { margin: 0; padding: 0; background: #fff; color: #111;
    font-family: 'Helvetica Neue', Arial, sans-serif; font-size: 10pt; line-height: 1.35; }
  .header { display: flex; align-items: flex-start; justify-content: space-between;
    border-bottom: 2px solid #111; padding-bottom: 10px; margin-bottom: 14px; }
  .shop { display: flex; align-items: center; gap: 10px; }
  .shop-logo { max-height: 0.9in; max-width: 1.5in; object-fit: contain; }
  .shop-text { display: flex; flex-direction: column; }
  .shop-name { font-weight: 700; font-size: 13pt; letter-spacing: 0.5px;
    text-transform: uppercase; }
  .shop-addr { font-size: 9pt; color: #444; }
  .meta-block { text-align: right; font-size: 10pt; }
  .meta-block .title { font-weight: 700; font-size: 14pt; margin-bottom: 4px; }
  .meta-block .stat { color: #444; }
  .meta-block .stat strong { color: #111; }
  .run-section-head { font-size: 12.5pt; font-weight: 700; margin: 22px 0 8px;
    padding-bottom: 5px; border-bottom: 2px solid #111; page-break-after: avoid; }
  .run-section-head:first-of-type { margin-top: 4px; }
  .summary { display: flex; gap: 16px; margin-bottom: 12px; font-size: 10pt; }
  .summary .pill { padding: 4px 12px; border-radius: 999px; border: 1px solid #ddd; }
  .summary .pill strong { color: #111; }
  .summary .pill.delivered { border-color: #047857; color: #047857; }
  .summary .pill.failed { border-color: #b91c1c; color: #b91c1c; }
  table { width: 100%; border-collapse: collapse; }
  thead th { text-align: left; font-size: 8.5pt; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.5px; color: #555;
    border-bottom: 1px solid #888; padding: 4px 6px; }
  tbody td { vertical-align: top; padding: 6px; border-bottom: 1px solid #eee; }
  tbody tr.row { page-break-inside: avoid; }
  td.ord { width: 11%; font-weight: 700; }
  td.ord .src { font-weight: 400; font-size: 8.5pt; color: #777; margin-top: 2px; }
  td.time { width: 11%; font-variant-numeric: tabular-nums; }
  td.recip { width: 32%; }
  td.recip .name { font-weight: 600; }
  td.recip .addr { color: #444; font-size: 9pt; margin-top: 2px; }
  td.meta { width: 28%; font-size: 9pt; color: #444; }
  td.meta .signedby { color: #111; font-weight: 500; }
  td.meta .phone { font-variant-numeric: tabular-nums; margin-top: 2px; }
  td.meta .notes { font-style: italic; margin-top: 3px; color: #555; }
  td.sig { width: 18%; text-align: center; }
  td.sig img { max-width: 100%; max-height: 56px; object-fit: contain;
    border: 1px solid #ddd; border-radius: 4px; padding: 2px; background: #fff; }
  .muted { color: #aaa; font-style: italic; }
  .snapshot-scope { border: 1px solid #aaa; padding: 8px; margin-bottom: 12px; font-size: 9pt; }
  .failures { margin-top: 18px; page-break-before: auto; }
  .failures h2 { font-size: 12pt; color: #b91c1c; margin: 0 0 8px; padding-bottom: 4px;
    border-bottom: 1px solid #b91c1c; }
  .failure { border: 1px solid #f4cdcd; border-left: 4px solid #b91c1c;
    padding: 8px 10px; margin-bottom: 8px; border-radius: 4px;
    page-break-inside: avoid; }
  .failure-head { display: flex; justify-content: space-between; }
  .failure-addr { color: #555; font-size: 9pt; margin: 2px 0 6px; }
  .failure-reason, .failure-notes { font-size: 9.5pt; }
  .failure-label { font-weight: 600; color: #444; }
  .footer { margin-top: 18px; padding-top: 10px; border-top: 1px solid #ddd;
    font-size: 8.5pt; color: #888; text-align: center; }
</style>
</head>
<body>
<header class="header">
  <div class="shop">
    ${logoSrc ? `<img class="shop-logo" src="${logoSrc}" alt="${shopName}" />` : ''}
    <div class="shop-text">
      <div class="shop-name">${shopName}</div>
      ${shopAddress ? `<div class="shop-addr">${shopAddress}</div>` : ''}
    </div>
  </div>
  <div class="meta-block">
    <div class="title">${esc(metaTitle || 'Delivery Manifest')}</div>
    <div class="stat">Driver: <strong>${esc(driverName || '—')}</strong></div>
    <div class="stat">Date: <strong>${esc(date ? fmtDate(date) : 'All dates')}</strong></div>
  </div>
</header>

${renderSnapshotScope(scope)}
${innerHtml}

<footer class="footer">
  Generated ${esc(fmtGeneratedAt())} · Las Vegas Bouquet Delivery System
</footer>

</body>
</html>`;

  if (!isCurrent()) return null;
  const w = popup || window.open('', '_blank', 'width=900,height=1100');
  if (!w) {
    toast.error('Pop-ups blocked — allow pop-ups to print');
    return;
  }
  if (w.closed) return null;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return scheduleManifestPrint(w, isCurrent, onAutoPrintSettled);
};

/**
 * @param {object} args
 * @param {object} args.run        — { id, name, driver_id, ... }
 * @param {string} args.date       — yyyy-mm-dd
 * @param {string} args.driverName — display name
 * @param {Array}  args.orders     — orders belonging to the run (any status)
 * @param {object} args.shop       — shop_settings response (logo_id, name, address)
 * @param {object} args.signatureUrls — { [storage_path]: dataUrl }
 */
export function printRunManifest({ run, date, driverName, orders, shop, signatureUrls = {}, popup, isCurrent, scope, onAutoPrintSettled }) {
  if (!run || !orders) {
    toast.error('Cannot print — missing run data');
    return;
  }

  return openManifestDocument({
    docTitle: `Delivery Manifest — ${run.name || ''}`,
    metaTitle: run.name,
    driverName,
    date,
    shop,
    popup, isCurrent, scope, onAutoPrintSettled,
    innerHtml: renderManifestBody(orders, signatureUrls),
  });
}

/**
 * @param {object} args
 * @param {string} args.driverName    — display name
 * @param {string} args.date          — yyyy-mm-dd
 * @param {Array}  args.runSections   — [{ name, orders }] ordered for display;
 *                                       each rendered as its own manifest block
 * @param {object} args.shop          — shop_settings response
 * @param {object} args.signatureUrls — { [storage_path]: dataUrl }
 */
export function printDriverManifest({ driverName, date, runSections, shop, signatureUrls = {}, popup, isCurrent, scope, onAutoPrintSettled }) {
  if (!runSections || runSections.length === 0) {
    toast.error('Cannot print — no runs for this driver');
    return;
  }

  const innerHtml = runSections.map(section => `
<div class="run-section-head">${esc(section.name || 'Run')}</div>
${renderManifestBody(section.orders, signatureUrls)}`).join('');

  return openManifestDocument({
    docTitle: `Driver Manifest — ${driverName || ''}`,
    metaTitle: 'Driver Manifest',
    driverName,
    date,
    shop,
    popup, isCurrent, scope, onAutoPrintSettled,
    innerHtml,
  });
}
